import { Component, OnInit, Input, ViewChild, Output, ViewEncapsulation, EventEmitter, OnDestroy } from '@angular/core';
import { IdService } from '../../shared/services/id.service';
import { isMobile } from '../../shared/utils/mobile';
import { SortLocale } from './sort.types';

@Component({
	selector: 'rbc-sort',
	templateUrl: './sort.component.html',
	styleUrls: ['./sort.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class SortComponent implements OnInit, OnDestroy {
	@Input() checkable: boolean;
	@Input() options;
	@Input() locale: SortLocale;

	id: string;
	activeIndex = null;
	newIndex = null;
	oldIndex = null;
	difference = 0;
	items = [];
	reorderedItems: any = [];
	timeout = null;
	height = 'auto';
	edgePositioned = false;

	@Output() sortUpdated = new EventEmitter();
	@ViewChild('container') $container;

	constructor(private idService: IdService) {
		this.id = idService.generate();
	}

	ngOnInit() {
		// create a copy of the items for display only
		if (this.options) {
			this.items = this.options;
			// copys the array not the reference
			this.reorderedItems = [...this.items];
		}
	}

	ngOnDestroy(): void {
		this.idService.remove(this.id);
	}

	beforeDestroy() {
		document.removeEventListener('touchend', this.setOnClick);
	}

	setOnClick(e) {
		// if the click is not in the sorting container than sort the set - This event listener will be attached to the document
		const container = this.$container;

		if (!(container === e.target || container.contains(e.target))) {
			this.set();
		}
	}

	newHeight() {
		this.height = this.$container.nativeElement.getBoundingClientRect().height + 'px';
	}

	set() {
		// replace the displayed array with the real rearranged results
		this.reorderedItems = [...this.items];

		// let the parent know what has happened today
		this.sortUpdated.emit({
			previousIndex: this.oldIndex,
			newIndex: this.newIndex,
			data: this.items
		});

		// reset everything to have a fresh start
		this.activeIndex = null;
		this.oldIndex = null;
		this.newIndex = null;
		this.difference = 0;
		this.timeout = null;

		document.removeEventListener('touchend', this.setOnClick);
	}

	checkStatic(index, direction) {
		// If static is true return true
		if (
			typeof this.reorderedItems[index] !== 'undefined' &&
			typeof this.reorderedItems[index].static !== 'undefined' &&
			!!this.reorderedItems[index].static
		) {
			return true;
		}
		// If up button has been pressed
		if (direction === 'up') {
			if (index + this.difference === 0 && this.items.length !== this.difference - 1) {
				return true;
			} else if (
				this.reorderedItems[index - 1] &&
				typeof this.reorderedItems[index - 1].static !== 'undefined' &&
				this.reorderedItems[index - 1].static === true
			) {
				return true;
			} else {
				return false;
			}
		}
		// If down button has been pressed
		if (direction === 'down') {
			if (index === this.reorderedItems.length || index + this.difference + 1 === this.items.length) {
				return true;
			} else if (
				typeof this.reorderedItems[index + 1] !== 'undefined' &&
				typeof this.reorderedItems[index + 1].static !== 'undefined' &&
				!!this.reorderedItems[index + 1].static
			) {
				return true;
			} else {
				return false;
			}
		}
	}

	setIndex(index, count) {
		// Index of item being moved and count is -1 for up and 1 for down
		// save the original index before moving array items around
		// as reference for the original items vs the reordered items
		if (this.oldIndex === null) {
			this.oldIndex = index;

			// If the element being moved is at the beginning or end of the list make it edge posititon
			if (this.oldIndex === 0 || this.oldIndex + 1 === this.items.length) {
				this.edgePositioned = true;
			}

			if (isMobile()) {
				// set a listener to set the new ordered list when clicked off
				document.addEventListener('touchend', this.setOnClick);
			}
		}

		// set new indexes to include the array difference
		this.activeIndex = this.oldIndex + this.difference;
		this.newIndex = this.activeIndex + count;
	}

	move(index) {
		// grab the selected item in the array
		const removed = this.items.splice(this.newIndex, 1)[0];
		// and move it to the new position
		this.items.splice(this.activeIndex, 0, removed);

		// if we're going up
		if (this.difference < 0) {
			// grab and add extra array items with the newly arranged items array
			const extras = this.reorderedItems.splice(0, 0 - this.difference - 1);
			this.reorderedItems = [...extras, ...this.items];

			// add one more array item to push list items down
			// and keep active item in same position
			this.reorderedItems.splice(0, 0, '');

			// if we're going down
		} else if (this.difference > 0) {
			this.reorderedItems = [...this.items];

			// shave off the difference from the top of the displayed array
			for (let i = 0; i < this.difference; i++) {
				this.reorderedItems.shift();
			}
		}

		// update reordered items to match
		this.reorderedItems = [...this.items];

		if (isMobile()) {
			clearTimeout(this.timeout);

			// set the new sorted list if the user has not clicked off within 2s
			this.timeout = setTimeout(() => {
				this.set();
			}, 2000);
		}
	}

	moveUp(index) {
		this.setIndex(index, -1);

		if (this.newIndex + 1 > 0) {
			this.difference = this.difference - 1;
			this.move(index);
		}
	}

	moveDown(index) {
		this.setIndex(index, 1);

		if (this.newIndex < this.items.length) {
			this.difference = this.difference + 1;
			this.move(index);
		}
	}
}
